import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ContactUsModal from "../components/ContactUs";
import { useDispatch, useSelector } from "react-redux";
import {
  markedRead,
  messageReceived,
  selectThread,
  selectThreadLoaded,
  selectUnread,
  threadLoaded,
} from "../redux/threadSlice";
import { loadCounts, selectHasInboxCounts, selectUnreadInboxCount } from "../redux/inboxSlice";
import { useAuth } from "../context/AuthContext.jsx";
import socket from "../shared/_helpers/socket";
import { getMyThread } from "../shared/_services/thread.services";
import { CHOOSE_PLAN_PATH, hasChatAccess } from "../shared/_helpers/chatGate";
import { RAIL_CHOOSE_PLAN } from "../components/intake/intakeCopy";
import { Logo, Tooltip, TooltipProvider, cn } from "../components/ui";
import StarsIcon from "../styles/StarsIcon";

const ADMIN_ROLES = ["super-admin", "admin"];
// One counts read per burst of `thread updated` events (R78), as InboxPage
// debounces its list refetch.
const COUNTS_REFRESH_DEBOUNCE_MS = 300;

function getCurrentUserFromStorage() {
  try {
    return JSON.parse(localStorage.getItem("currentUser"))?.user || {};
  } catch (error) {
    return {};
  }
}

function isPrivilegedAdmin(role) {
  return ADMIN_ROLES.includes(role);
}

function isClientsPathActive(pathname, currentUserDataId) {
  const matchedClientPaths = currentUserDataId
    ? [`/dashboard/${currentUserDataId}`, `/meetings/${currentUserDataId}`, `/documents/${currentUserDataId}`]
    : [];
  return pathname === "/users" || matchedClientPaths.includes(pathname);
}

function getLogoPath(role) {
  if (role === "client") return "/dashboard";
  if (isPrivilegedAdmin(role)) return "/admin-dashboard";
  if (role === "advisor") return "/inbox";
  return null;
}

function isMeetingsActive(pathname) {
  return ["/meetings", "/advisor-meetings", "/advisor-manage-availability"].includes(pathname);
}

// SP8 (R453): the bar under 820px holds five items. An item may carry a short
// `barLabel` for it and `inBar: false` to leave the bar (the rail keeps all).
function buildSidebarItems({ role, pathname, currentUserDataId, unseenCount, inboxUnreadCount, paid }) {
  const items = [];

  if (role === "client") {
    items.push({
      key: "client-dashboard",
      to: "/dashboard",
      icon: "dashboard",
      label: "Dashboard",
      active: pathname === "/dashboard",
      barLabel: "Home",
    });
  }

  // Pooled inbox SP4, decision 29 (variant A): the advisors' thread is
  // offered only to a paid client; an unpaid client gets "Choose your plan"
  // in its place (the package page until SP5).
  if (role === "client" && paid) {
    items.push({
      key: "advisors",
      to: "/thread",
      icon: "message",
      label: "Inbox",
      active: pathname === "/thread",
      badge: unseenCount > 0 ? unseenCount : null,
    });
  }

  if (role === "client" && !paid) {
    items.push({
      key: "choose-plan",
      to: CHOOSE_PLAN_PATH,
      icon: "card",
      label: RAIL_CHOOSE_PLAN,
      barLabel: "Plan",
      active: pathname === CHOOSE_PLAN_PATH,
    });
  }

  if (isPrivilegedAdmin(role)) {
    items.push({
      key: "admin-dashboard",
      to: "/admin-dashboard",
      icon: "dashboard",
      label: "Dashboard",
      active: pathname === "/admin-dashboard",
    });
  }

  if (role === "advisor") {
    items.push({
      key: "inbox",
      to: "/inbox",
      icon: "message",
      label: "Inbox",
      active: pathname === "/inbox" || pathname.startsWith("/inbox/"),
      badge: inboxUnreadCount > 0 ? inboxUnreadCount : null,
    });
  }

  if (!isPrivilegedAdmin(role)) {
    items.push({
      key: "meetings",
      to: role === "advisor" ? "/advisor-meetings" : "/meetings",
      icon: "calendar",
      label: "Meetings",
      active: isMeetingsActive(pathname),
    });
  }

  if (["super-admin", "advisor", "admin"].includes(role)) {
    items.push({
      key: "clients",
      to: "/users",
      icon: "users",
      label: "Clients",
      active: isClientsPathActive(pathname, currentUserDataId),
    });
  }

  if (isPrivilegedAdmin(role)) {
    items.push({
      key: "advisors",
      to: "/advisors",
      icon: "user",
      label: "Advisors",
      active: pathname === "/advisors",
    });
  }

  if (role === "super-admin") {
    items.push({
      key: "packages",
      to: "/package-management",
      icon: "card",
      label: "Packages",
      active: pathname === "/package-management",
    });
    items.push({
      key: "admins",
      to: "/admins",
      icon: "settings",
      label: "Admin",
      inBar: false,
      active: pathname === "/admins",
    });
  }

  if (role === "client") {
    items.push({
      key: "documents",
      to: "/documents",
      icon: "file",
      label: "Documents",
      barLabel: "Docs",
      active: pathname === "/documents",
    });
    items.push({
      key: "questionnaire",
      to: "/questionnaire",
      icon: "clipboard",
      label: "Questionnaire",
      inBar: false,
      active: pathname === "/questionnaire",
    });
  }

  items.push({
    key: "profile",
    to: "/profile",
    icon: "circleUser",
    label: "Profile",
    active: pathname === "/profile",
  });

  return items;
}

const DashboardSidebar = () => {
  const path = useLocation();

  const [contactUsPopupShow, setContactUsPopupShow] = useState(false);
  // SEC-066: which admin menu items render is gated by the server-verified
  // role, not the localStorage copy — this is rendered inside
  // ProtectedRoutes, so verification has already settled by the time this
  // mounts.
  const { role, user } = useAuth();
  const currentUserDataId = getCurrentUserFromStorage()?.data?._id;
  // Pooled inbox SP1 (W4): the badge comes from ReadState via threadSlice's
  // selectUnread (lastAdvisorMessageAt > lastReadAt), not the old chat-list
  // based unread count.
  const unread = useSelector(selectUnread);
  const dispatch = useDispatch();
  // SP2-W14, owner decision B: the client's unread badge must update live on
  // every client page, not just /dashboard and /thread. DashboardSidebar is
  // mounted on every DashboardLayout route, so it is the one live listener —
  // mirroring the advisor's `thread updated` pattern below. On mount, load
  // the thread once if this store has never held one (so the badge has
  // something to read on whichever page loads first), then register a
  // "message received" listener for the life of the mount.
  const threadHasLoaded = useSelector(selectThreadLoaded);
  // SP5: only a client with chat access has a thread to read — the server
  // answers anyone else 402 plan_required (spec § The chat gate).
  const chat = hasChatAccess(user);
  useEffect(() => {
    if (role !== "client" || threadHasLoaded || !chat) return undefined;
    getMyThread()
      .then((res) => dispatch(threadLoaded(res.data)))
      .catch(() => {});
    return undefined;
  }, [role, threadHasLoaded, chat, dispatch]);
  useEffect(() => {
    if (role !== "client") return undefined;
    const onMessage = (m) => dispatch(messageReceived(m));
    socket.on("message received", onMessage);
    return () => socket.off("message received", onMessage);
  }, [role, dispatch]);
  // SP2-W15: the backend (SP2-B15) emits 'thread read' to the thread room
  // after any successful mark-read, so reading the thread in another tab (or
  // the client's own screen elsewhere) clears this badge too. Only an event
  // naming the signed-in client's own participant id, for the thread this
  // store has loaded, may do that — an advisor reading the same thread must
  // not clear the client's badge. `thread` is read via a ref rather than a
  // dependency so the listener doesn't re-subscribe on every lastReadAt
  // change; the ref is refreshed on every render, ahead of the effect.
  const thread = useSelector(selectThread);
  const threadRef = useRef(null);
  threadRef.current = thread;
  useEffect(() => {
    if (role !== "client") return undefined;
    const onThreadRead = (payload) => {
      if (payload.participant !== user?._id) return;
      if (payload.threadId !== threadRef.current?._id) return;
      dispatch(markedRead(payload.lastReadAt));
    };
    socket.on("thread read", onThreadRead);
    return () => socket.off("thread read", onThreadRead);
  }, [role, user, dispatch]);
  // Pooled inbox SP2 (W3, R66): the advisor's Inbox badge is the server's
  // unfiltered unread count (counts.unread). Any list load fills it; when an
  // advisor lands on a page with no counts yet, one light load fetches them.
  // Not on /inbox itself: the page's own list load fills the counts there.
  const inboxUnreadCount = useSelector(selectUnreadInboxCount);
  const hasInboxCounts = useSelector(selectHasInboxCounts);
  const onInbox = path.pathname.startsWith("/inbox");
  useEffect(() => {
    if (role === "advisor" && !hasInboxCounts && !onInbox)
      dispatch(loadCounts());
  }, [role, hasInboxCounts, onInbox, dispatch]);
  // R78: the badge follows the inbox on every advisor page. On /inbox the
  // page's own list refetch carries the counts; elsewhere the sidebar reads
  // them again, debounced, on `thread updated` (the advisors:inbox room).
  useEffect(() => {
    if (role !== "advisor" || onInbox) return undefined;
    let timer = null;
    const onThreadUpdated = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        dispatch(loadCounts({ force: true }));
      }, COUNTS_REFRESH_DEBOUNCE_MS);
    };
    socket.on("thread updated", onThreadUpdated);
    return () => {
      clearTimeout(timer);
      socket.off("thread updated", onThreadUpdated);
    };
  }, [role, onInbox, dispatch]);
  const logoPath = getLogoPath(role);
  const sidebarItems = buildSidebarItems({
    role,
    pathname: path.pathname,
    currentUserDataId,
    unseenCount: unread ? 1 : 0,
    inboxUnreadCount,
    paid: chat,
  });
  const handleContactUsClick = () => {
    setContactUsPopupShow(true);
  };

  // SP8 (R399): ONE navigation. On a desktop it is the 96px navy rail; under
  // 820px the same <nav> is the fixed tab bar at the foot (DESIGN.md :499,
  // :591), with the same links in the same order. The brand mark hides there;
  // Contact Us floats above the bar (R400).
  return (
    <aside
      className={cn(
        "stars-dark stars-tabbar tw-z-40 tw-bg-overlay-navy tw-font-sans tw-text-text",
        "tw-border-0 tw-border-solid tw-border-divider",
        "rail:tw-sticky rail:tw-top-0 rail:tw-flex rail:tw-h-screen rail:tw-w-24 rail:tw-flex-none rail:tw-flex-col rail:tw-items-center rail:tw-gap-8 rail:tw-py-6 rail:tw-border-r",
        "max-rail:tw-fixed max-rail:tw-inset-x-0 max-rail:tw-bottom-0 max-rail:tw-border-t max-rail:tw-bg-overlay-navy/95",
      )}
    >
      {logoPath && (
        <Link
          to={logoPath}
          className="tw-rounded-pill max-rail:tw-hidden focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-focus-ring"
        >
          <Logo variant="mark" className="tw-w-9" />
        </Link>
      )}
      <nav aria-label="Main" className="tw-w-full">
        <ul className="tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0 max-rail:tw-grid-flow-col max-rail:tw-auto-cols-fr max-rail:tw-gap-0 max-rail:tw-px-1 max-rail:tw-pt-1">
          {sidebarItems.map((item) => (
            <li
              key={item.key}
              className={cn(
                "tw-flex tw-min-w-0 tw-justify-center",
                item.inBar === false && "max-rail:tw-hidden",
              )}
            >
              <Link
                to={item.to}
                aria-current={item.active ? "page" : undefined}
                className={cn(
                  "tw-group tw-grid tw-min-w-0 tw-justify-items-center rail:tw-w-full tw-gap-1 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-no-underline",
                  "tw-transition-colors tw-duration-fast tw-ease-out max-rail:tw-w-full max-rail:tw-min-h-12 max-rail:tw-py-1",
                  "focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-focus-ring",
                  item.active ? "tw-text-text" : "tw-text-text-muted hover:tw-text-text",
                )}
              >
                <span
                  className={cn(
                    "tw-relative tw-grid tw-h-8 tw-w-11 tw-place-items-center tw-rounded-pill tw-transition-colors tw-duration-fast",
                    item.active ? "tw-bg-overlay-white/10" : "group-hover:tw-bg-overlay-white/5",
                  )}
                >
                  <StarsIcon name={item.icon} className="tw-h-5 tw-w-5" />
                  {item.badge && (
                    <span
                      data-slot="count"
                      className="tw-absolute -tw-top-1 tw-right-0 tw-min-w-5 tw-rounded-pill tw-bg-text-accent tw-px-1 tw-text-xs tw-font-bold tw-text-on-accent tw-ring-2 tw-ring-overlay-navy"
                    >
                      {item.badge}
                    </span>
                  )}
                </span>
                {item.barLabel ? (
                  <>
                    <span
                      data-slot="rail-label"
                      className="tw-block tw-max-w-full tw-truncate max-rail:tw-hidden"
                    >
                      {item.label}
                    </span>
                    <span
                      data-slot="bar-label"
                      className="tw-block tw-max-w-full tw-truncate rail:tw-hidden"
                    >
                      {item.barLabel}
                    </span>
                  </>
                ) : (
                  <span
                    data-slot="rail-label"
                    className="tw-block tw-max-w-full tw-truncate"
                  >
                    {item.label}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      {role !== "super-admin" && (
        <div className="rail:tw-mt-auto max-rail:tw-fixed max-rail:tw-bottom-16 max-rail:tw-right-4">
          <TooltipProvider>
            <Tooltip
              side="right"
              content="Having problems with your dashboard? Connect with Customer Support."
            >
              <button
                type="button"
                onClick={handleContactUsClick}
                className={cn(
                  "tw-grid tw-w-20 tw-cursor-pointer tw-justify-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-py-2 tw-text-xs tw-font-semibold tw-text-text-muted hover:tw-text-text",
                  "focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-focus-ring",
                  "max-rail:tw-h-12 max-rail:tw-w-12 max-rail:tw-place-items-center max-rail:tw-rounded-pill max-rail:tw-bg-overlay-navy max-rail:tw-shadow-2",
                )}
              >
                <StarsIcon name="circleHelp" className="tw-h-5 tw-w-5" />
                <span className="max-rail:tw-sr-only">Contact Us</span>
              </button>
            </Tooltip>
          </TooltipProvider>
          {contactUsPopupShow && <ContactUsModal onClose={() => setContactUsPopupShow(false)} />}
        </div>
      )}
    </aside>
  );
};
export default DashboardSidebar;
